import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import type { VFormFilterButtonProps } from '@modules/form-widgets/components/VFormFilterButton/types';
import type { IReportTabItem } from '@modules/reports/types';
import { useI18n } from '@modules/i18n/composables';

import type { IReportTabsEmits } from '../types';

export interface ReportTabsComposable {
  buttons: ComputedRef<VFormFilterButtonProps[]>;
  onToggle(index: number): void;
  onRemove(index: number): void;
  onSelectItem(index: number): void;
  onAdd(): void;
}

export default function useReportTabs(
  tabs: Ref<IReportTabItem[] | undefined>,
  translationKey: Ref<symbol>,
  addNoPointerEvents: Ref<boolean>,
  emit: IReportTabsEmits,
): ReportTabsComposable {
  const { getTranslation } = useI18n();

  const buttons = computed<VFormFilterButtonProps[]>(() => (
    tabs.value?.map((item) => ({
      type: item.isActive ? 'secondary' : 'transparent-secondary',
      allowExpand: item.allowExpand,
      expanded: item.expanded,
      label: item.name || getTranslation(translationKey.value, item.shortcode, { id: item.id }).value,
      allowRemove: item.allowRemove,
      isClickable: !item.isActive,
      noBorder: item.noBorder,
      noPointerEvents: item.noPointerEvents ?? addNoPointerEvents.value,
      removeIcon: item.removeIcon,
    } as VFormFilterButtonProps)) ?? []
  ));

  function onToggle(index: number): void {
    const tab = tabs.value?.[index];

    if (tab) {
      emit('expand-toggle', tab.id);
    }
  }

  function onRemove(index: number): void {
    const tab = tabs.value?.[index];

    if (tab) {
      emit('remove', tab.id, tab.name);
    }
  }

  function onSelectItem(index: number): void {
    const tab = tabs.value?.[index];

    if (tab) {
      emit('select', tab.id);
    }
  }

  function onAdd(): void {
    emit('add');
  }

  return {
    buttons,
    onToggle,
    onRemove,
    onSelectItem,
    onAdd,
  };
}
